import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
} from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PasswordStrength } from 'app/services/api5-service/api.interface';
import { BehaviorSubject, fromEvent, timer } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';

import { HTMLIspuiPasswordElement } from '@ispui/input/dist/';

import { DocHelper } from 'utils/dochelper';

import { getPasswordStrength } from './password.utils';

import { ISPFieldType } from '../../model';
import { ISPFieldTypeBase } from '../field-type-base.class';
import { setAutofocus } from '../input/input.utils';

/**
 * `@type=password` password field component
 *
 * Use only with Formly
 */
@UntilDestroy()
@Component({
  selector: 'isp-formly-password-field',
  templateUrl: './password.component.html',
  styleUrls: ['./scss/password.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordFieldComponent
  extends ISPFieldTypeBase<ISPFieldType.Password>
  implements AfterViewInit
{
  /** Password strength */
  private readonly passwordStrengthSubject: BehaviorSubject<
    PasswordStrength | undefined
  > = new BehaviorSubject(undefined);

  /** Password field link */
  @ViewChild('ispuiPassword')
  ispuiPasswordRef: ElementRef<HTMLIspuiPasswordElement>;

  @ViewChild('passwordField')
  passwordRef: ElementRef<HTMLInputElement>;

  readonly passwordStrength$ = this.passwordStrengthSubject.asObservable();

  /** Password strength msg */
  readonly passwordStrengthMsg$ = this.passwordStrength$.pipe(
    switchMap(strength =>
      this.formState.docSubject.pipe(
        map(doc => DocHelper.getPasswordStrengthMessage(strength, doc)),
      ),
    ),
  );

  ngAfterViewInit(): void {
    if (this.props.autofocus) {
      setAutofocus(this.passwordRef.nativeElement, this.field, this.formState);
    }

    this.formControl.statusChanges
      .pipe(
        switchMap(() => {
          // @HACK ispui-input do not check for invalid flag change, so we need to trigger it manualy
          const ispuiInput = this.passwordRef.nativeElement.closest(
            'ispui-input',
          ) as HTMLIspuiInputElement;
          // @HACK because we need to wait, until stencil detect that 'invalid' attribute was changed
          return timer(0).pipe(
            tap(() => {
              ispuiInput?.validateInput();
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();

    this.formControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.passwordStrengthSubject.next(getPasswordStrength(value));
        this.passwordRef.nativeElement.dispatchEvent(new Event('change'));
      });

    // small time debounce in order to input element update it's value property after keydown event (Firefox case!)
    fromEvent(this.passwordRef.nativeElement, 'keydown')
      .pipe(debounceTime(10), untilDestroyed(this))
      .subscribe((event: InputEvent) => {
        this.passwordStrengthSubject.next(
          getPasswordStrength((event.target as HTMLInputElement).value),
        );
      });
  }

  /**
   * Handler for generate password button click
   */
  generatePassword(): void {
    this.ispuiPasswordRef.nativeElement.animatePasswordGeneration();
  }

  /**
   * Generate password handler
   *
   * @param event - custom event, contains generated password
   */
  handlePasswordGenerate(event: CustomEvent): void {
    this.formControl.setValue(event.detail.value);
    const confirmName = this.props.confirmControlName;
    if (confirmName && this.form.controls[confirmName]) {
      this.form.patchValue({ [confirmName]: event.detail.value });
    }
  }
}
